import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram } from "lucide-react";
import { getSharedPublic } from "@/lib/api";
import { useLocale } from "@/context/LocaleContext";
import Newsletter from "@/components/Newsletter";
import { resolveField } from "@/theme/sectionDefs";
import useThemeConfig from "@/theme/useThemeConfig";

const DEFAULT_LOGO = "/brand/levami-logo.png";

/* Footer link columns now come from the Theme Editor (/admin/theme > Pied de
   page), fully reorderable/editable there. This default mirrors the backend's
   DEFAULT_THEME so the footer renders identically before the theme config loads. */
const DEFAULT_FOOTER_THEME = {
  link_columns: [
    {
      id: "footer-col-boutique", title: "",
      links: [
        { id: "footer-boutique-nouveautes", label: "", url: "/boutique/nouveautes" },
        { id: "footer-boutique-keffiehs", label: "", url: "/boutique/keffiehs" },
        { id: "footer-boutique-robes", label: "", url: "/boutique/robes-abayas" },
        { id: "footer-boutique-brodees", label: "", url: "/boutique/pieces-brodees" },
        { id: "footer-boutique-chales", label: "", url: "/boutique/chales-ponchos" },
        { id: "footer-boutique-all", label: "", url: "/boutique" },
      ],
    },
    {
      id: "footer-col-decouvrir", title: "",
      links: [
        { id: "footer-decouvrir-histoire", label: "", url: "/histoire" },
        { id: "footer-decouvrir-artisanat", label: "", url: "/artisanat" },
        { id: "footer-decouvrir-lookbook", label: "", url: "/lookbook" },
        { id: "footer-decouvrir-journal", label: "", url: "/journal" },
        { id: "footer-decouvrir-ateliers", label: "", url: "/ateliers" },
        { id: "footer-decouvrir-partenariats", label: "", url: "/partenariats" },
        { id: "footer-decouvrir-contact", label: "", url: "/contact" },
      ],
    },
    {
      id: "footer-col-aide", title: "",
      links: [
        { id: "footer-aide-livraison", label: "", url: "/livraison-retours" },
        { id: "footer-aide-suivi", label: "", url: "/suivi-commande" },
        { id: "footer-aide-faq", label: "", url: "/faq" },
        { id: "footer-aide-cgv", label: "", url: "/conditions-generales-de-vente" },
        { id: "footer-aide-confidentialite", label: "", url: "/confidentialite" },
        { id: "footer-aide-cookies", label: "", url: "/cookies" },
        { id: "footer-aide-mentions", label: "", url: "/mentions-legales" },
      ],
    },
  ],
  newsletter_enabled: true,
};

export default function Footer() {
  const { t } = useLocale();
  const [settings, setSettings] = useState({
    contact_email: "levamifr@gmail.com",
    whatsapp_link: "https://wa.me/33758810404",
    instagram: "@levami_store",
    instagram_link: "https://www.instagram.com/levami_store/",
    logo_url: DEFAULT_LOGO,
  });
  const footerTheme = useThemeConfig("footer", DEFAULT_FOOTER_THEME);

  useEffect(() => {
    let active = true;
    getSharedPublic("/content/general_settings").then((res) => {
      if (active && res.data?.data) setSettings((s) => ({ ...s, ...res.data.data }));
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  return (
    <footer className="bg-levami-ivoire border-t border-levami-sable mt-24" data-testid="site-footer">
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 mb-14">
          <div className="lg:col-span-2">
            <img src={settings.logo_url} alt="LEVAMI" className="h-12 w-auto object-contain mb-4" />
            <p className="font-serif-heading text-lg text-levami-charbon mb-1">{t("footer.tagline")}</p>
            <p className="text-sm text-levami-brun/70 max-w-xs mb-4">
              {t("footer.description")}
            </p>
            <a
              href={settings.instagram_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-levami-brun hover:text-levami-primary"
              data-testid="footer-brand-instagram-link"
            >
              <Instagram className="h-4 w-4" /> {settings.instagram}
            </a>
          </div>
          {(footerTheme.link_columns || []).map((col) => (
            <div key={col.id}>
              <p className="text-xs tracking-label uppercase text-levami-brun/60 mb-3">{resolveField(col.id, "title", col, t)}</p>
              <ul className="space-y-2 text-sm text-levami-brun">
                {(col.links || []).map((link) => (
                  <li key={link.id}><Link to={link.url} className="hover:text-levami-primary">{resolveField(link.id, "label", link, t)}</Link></li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {footerTheme.newsletter_enabled !== false && (
          <div className="border-t border-levami-sable pt-10 mb-10">
            <p className="text-xs tracking-label uppercase text-levami-brun/60 mb-3 text-center">{t("footer.newsletter_heading")}</p>
            <Newsletter variant="footer" />
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-levami-sable text-xs text-levami-brun/60">
          <p>© {new Date().getFullYear()} LEVAMI — {t("footer.rights")}</p>
          <div className="flex items-center gap-4">
            <Link to="/pro?tab=register" className="hover:text-levami-primary" data-testid="footer-espace-pro-link">{t("nav.espace_pro")}</Link>
            <a href={`mailto:${settings.contact_email}`} className="hover:text-levami-primary" data-testid="footer-email-link">{settings.contact_email}</a>
            <a href={settings.whatsapp_link} target="_blank" rel="noopener noreferrer" className="hover:text-levami-primary" data-testid="footer-whatsapp-link">WhatsApp</a>
            <a href={settings.instagram_link} target="_blank" rel="noopener noreferrer" className="hover:text-levami-primary" data-testid="footer-instagram-link">Instagram</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
